/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from "react-redux";
import {
    Button,
    Stack,
    Paper,
    Typography,
    Divider,
    Grid,
    Box,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Formik, useFormikContext } from 'formik';
import get from "lodash/get";
import map from "lodash/map";
import find from "lodash/find";
import isEmpty from "lodash/isEmpty";

import Layout from "../../../components/layout/Layout";
import Switch from "../../../components/form/Switch";
import InputText from "../../../components/form/InputText";
import Autocomplete from "../../../components/form/AutoComplete";
import Notification from "../../../components/form/Notification";
import Alert from "../../../components/form/Alert";
import Skeleton from "../../../components/form/Skeleton";
import validator from "./validator";
import { useCanView } from "../../../config/hook/UserPermissions";

import { getBillingCodeId } from "../../../store/masters/thunk/billingCode/getId";
import { postBillingCode } from "../../../store/masters/thunk/billingCode/post";
import { putBillingCode } from "../../../store/masters/thunk/billingCode/put";
import { getBillingCodeOdoo } from "../../../store/masters/thunk/billingCode/getOdooProducts";

// Sincroniza odooProductName cuando cambia odooProductInternal
const OdooProductSync = ({ options }) => {
    const { values, setFieldValue } = useFormikContext();

    useEffect(() => {
        const selected = find(options, ({ value }) => value === values.odooProductInternal);
        if (selected !== undefined) {
            setFieldValue("odooProductName", get(selected, "productName", ""));
        }
    }, [values.odooProductInternal]);

    return null;
};

const NewEdit = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const canView = useCanView();
    const [__] = useTranslation("billingCode");
    const { id } = useParams();
    const submittedCodeRef = useRef("");
    const [showNoti, setShowNoti] = useState({ open: false, msg: "", variant: "error", action: "post" });
    const [alert, setAlert] = useState({ open: false, title: "", subtitle: "", type: "" });

    const current = useSelector(state => state.masters.billingCode.detail);
    const put = useSelector(state => state.masters.billingCode.put);
    const post = useSelector(state => state.masters.billingCode.post);
    const odooProducts = useSelector(state => state.masters.billingCode.odooProducts);

    const optionsOdooProducts = map(get(odooProducts, "data.result", []), ({ id: pid, name }) => ({
        value: pid,
        label: `${pid} - ${name}`,
        productName: name,
    }));

    const closeAlert = () => {
        setAlert({ open: false, title: "", subtitle: "", type: "", btn: "" });
    }

    const setError = (err) => {
        if (!isEmpty(err) && !!get(err, "Message", "")) {
            setAlert({
                open: true,
                title: get(err, "Message", ""),
                subtitle: (<ul>{map(get(err, "ValidationError", []), (item) => <li>{`• ${item}`}</li>)}</ul>),
                type: "error",
                func: closeAlert
            });
        } else {
            setAlert({ open: true, type: "default", func: closeAlert });
        }
    }

    useEffect(() => {
        if (get(post, "isSuccess", false)) {
            setShowNoti({ open: true, msg: "", variant: "success", action: "post" });
            navigate(`/config/billing-codes/${submittedCodeRef.current}`);
        }
        if (get(post, "isReject", false)) { setError(get(post, "error", {})); }
        if (get(put, "isSuccess", false)) { setShowNoti({ open: true, msg: "", variant: "success", action: "put" }); }
        if (get(put, "isReject", false)) { setError(get(put, "error", {})); }
    }, [put, post]);

    useEffect(() => {
        if (!get(odooProducts, "isSuccess", false)) {
            dispatch(getBillingCodeOdoo());
        }
        if (!!id) {
            dispatch(getBillingCodeId({ id }));
        }
    }, [dispatch, id]);

    const onSubmit = (values) => {
        const permission = id ? "update" : "add";
        if (canView("CONFIGURACIÓN/CODIGOS FACTURACION", permission)) {
            const selectedProduct = find(optionsOdooProducts, ({ value }) => value === values.odooProductInternal);
            const body = {
                coD_FACTURA: values.codFactura,
                description: values.description,
                isActive: Boolean(values.isActive),
                odooProductInternal: values.odooProductInternal ? Number(values.odooProductInternal) : null,
                odooProductName: get(selectedProduct, "productName") || values.odooProductName || null,
            };
            if (id) {
                dispatch(putBillingCode({ ...body, coD_FACTURA: id }));
            } else {
                submittedCodeRef.current = values.codFactura;
                dispatch(postBillingCode(body));
            }
        } else {
            setAlert({
                open: true,
                title: "Permiso Denegado.",
                subtitle: (<ul> • No tienes permiso para {id ? "actualizar" : "crear"} un código de facturación.</ul>),
                type: "error",
                func: closeAlert
            });
        }
    }

    const initialValues = {
        isActive: id ? get(current, "data.isActive", true) : true,
        codFactura: id ? get(current, "data.coD_FACTURA", "") : "",
        description: id ? get(current, "data.description", "") : "",
        odooProductInternal: id ? get(current, "data.odooProductInternal", "") || "" : "",
        odooProductName: id ? get(current, "data.odooProductName", "") || "" : "",
    }

    const isSubmitting = get(post, "isLoading", false) || get(put, "isLoading", false);

    return (
        <Layout
            propsToolbar={{
                title: !id ? __(`head.new.name`) : __(`head.edit.name`),
                srute: !id ? __(`head.new.code`) : id
            }}
        >
            {id && get(current, "isLoading")
                ? <Skeleton />
                : (
                    <Formik
                        enableReinitialize
                        initialValues={initialValues}
                        validationSchema={validator(__)}
                        onSubmit={onSubmit}
                    >
                        {formik => (
                            <form onSubmit={get(formik, "handleSubmit")}>
                                <OdooProductSync options={optionsOdooProducts} />
                                <Paper className='p-4'>
                                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                                        <LoadingButton
                                            className='w-[100px] min-w-[100px]'
                                            variant="contained"
                                            color="secondary"
                                            type="submit"
                                            loading={isSubmitting}
                                        >
                                            {__(`action.save`)}
                                        </LoadingButton>
                                        <Button
                                            className='w-[100px] min-w-[100px]'
                                            variant='outlined'
                                            color="secondary"
                                            onClick={() => navigate("/config/billing-codes")}
                                            disabled={isSubmitting}
                                        >
                                            {__(`action.cancel`)}
                                        </Button>
                                    </Stack>
                                </Paper>

                                <Paper className='mt-4'>
                                    <Stack className='p-4' direction="row" spacing={2} justifyContent="space-between">
                                        <Typography component="h4" variant="h4">{__(`labels.1`)}</Typography>
                                        <Switch formik={formik} name="isActive" __={__} />
                                    </Stack>
                                    <Divider />
                                    <Box className='p-4'>
                                        <Grid container spacing={{ xs: 2, md: 3 }}>
                                            <Grid item xs={12} sm={6}>
                                                <InputText
                                                    formik={formik}
                                                    name="codFactura"
                                                    label={__(`form.codFactura.label`)}
                                                    placeholder={__(`form.codFactura.placeholder`)}
                                                    disabled={isSubmitting || (!!id && get(current, "isLoading", false))}
                                                    readOnly={!!id}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <InputText
                                                    formik={formik}
                                                    name="description"
                                                    label={__(`form.description.label`)}
                                                    placeholder={__(`form.description.placeholder`)}
                                                    disabled={isSubmitting || get(current, "isLoading", false)}
                                                    multiline
                                                    rows={3}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Autocomplete
                                                    key={`odoo-${formik.values.odooProductInternal}-${optionsOdooProducts.length > 0}`}
                                                    formik={formik}
                                                    name="odooProductInternal"
                                                    label={__(`form.odooProductInternal.label`)}
                                                    placeholder={__(`form.odooProductInternal.placeholder`)}
                                                    disabled={isSubmitting || get(current, "isLoading", false)}
                                                    loading={get(odooProducts, "isLoading")}
                                                    options={optionsOdooProducts}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <InputText
                                                    formik={formik}
                                                    name="odooProductName"
                                                    label={__(`form.odooProductName.label`)}
                                                    disabled
                                                />
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </Paper>
                            </form>
                        )}
                    </Formik>
                )
            }
            <Notification __={__} showNoti={showNoti} setShowNoti={setShowNoti} />
            <Alert
                title={get(alert, "title")}
                subtitle={get(alert, "subtitle")}
                btn1={{ func: get(alert, "func") }}
                btn2={{}}
                type={get(alert, "type")}
                openAlert={get(alert, "open")}
                closeAlert={closeAlert}
            />
        </Layout>
    )
}

export default NewEdit
